import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Sparkles, Search, Trash2, FileText, Loader2, Check, X, ExternalLink, TrendingUp, Upload, Mail, Copy, Users2 } from "lucide-react";

const GRADE_TONE = (g) => {
  if (g.startsWith("A")) return "bg-emerald-100 text-emerald-900 border-emerald-300";
  if (g.startsWith("B")) return "bg-sky-100 text-sky-900 border-sky-300";
  if (g.startsWith("C")) return "bg-amber-100 text-amber-900 border-amber-300";
  if (g.startsWith("D")) return "bg-orange-100 text-orange-900 border-orange-300";
  return "bg-rose-100 text-rose-900 border-rose-300";
};

export default function AuditConsole() {
  const [form, setForm] = useState({ business_name: "", industry: "", city: "", prospect_email: "" });
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [reports, setReports] = useState([]);
  const [configured, setConfigured] = useState(null);
  const [bulkJob, setBulkJob] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const fileRef = useRef(null);

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return toast.error("CSV needs a header row + at least one data row.");
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idx = {
      business_name: headers.findIndex((h) => h.includes("business") || h === "name"),
      industry: headers.findIndex((h) => h.includes("industry") || h.includes("service")),
      city: headers.findIndex((h) => h.includes("city") || h.includes("location")),
      prospect_email: headers.findIndex((h) => h.includes("email")),
    };
    if (idx.business_name < 0 || idx.industry < 0 || idx.city < 0) {
      return toast.error("CSV must have columns: business_name, industry, city (email optional).");
    }
    const rows = lines.slice(1).map((line) => {
      const cells = line.split(",").map((c) => c.trim().replace(/^"(.*)"$/, "$1"));
      return {
        business_name: cells[idx.business_name] || "",
        industry: cells[idx.industry] || "",
        city: cells[idx.city] || "",
        prospect_email: idx.prospect_email >= 0 ? (cells[idx.prospect_email] || null) : null,
      };
    }).filter((r) => r.business_name && r.industry && r.city);
    if (rows.length === 0) return toast.error("No valid rows in CSV.");
    if (rows.length > 100) return toast.error("Max 100 audits per bulk job.");
    if (!window.confirm(`Run ${rows.length} audits in bulk?`)) return;
    try {
      const { data } = await api.post("/audit-console/bulk", { rows });
      toast.success(`Bulk started · ${data.total} queued`);
      setBulkJob({ id: data.job_id, total: data.total, completed: 0, failed: 0, status: "running" });
    } catch (err) { toast.error(formatApiError(err)); }
    if (fileRef.current) fileRef.current.value = "";
  };

  useEffect(() => {
    if (!bulkJob || bulkJob.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/audit-console/bulk/${bulkJob.id}`);
        setBulkJob(data);
        if (data.status !== "running") {
          toast.success(`Bulk done · ${data.completed}/${data.total}${data.failed ? ` · ${data.failed} failed` : ""}`);
          await loadReports();
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [bulkJob?.id, bulkJob?.status]);

  const emailReport = async (report) => {
    const to = report.prospect_email || window.prompt("Send audit to which email?", "");
    if (!to || !to.includes("@")) return;
    setEmailing(true);
    try {
      const { data } = await api.post(`/audit-console/reports/${report.id}/email`, { to });
      toast.success(`Emailed to ${to}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEmailing(false); }
  };

  const copyShareLink = async (report) => {
    if (!report.share_token) return toast.error("Old report — no share token. Re-run to enable sharing.");
    const url = `${window.location.origin}/audit/public/${report.id}/${report.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch { toast.error("Copy failed: " + url); }
  };

  const loadReports = async () => {
    try {
      const { data } = await api.get("/audit-console/reports");
      setReports(data.reports || []);
    } catch (e) { /* silent */ }
  };
  const loadConfig = async () => {
    try {
      const { data } = await api.get("/admin/audit-console/config");
      setConfigured(data.configured);
    } catch { setConfigured(false); }
  };
  useEffect(() => { loadReports(); loadConfig(); }, []);

  const runAudit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.industry.trim() || !form.city.trim()) {
      return toast.error("Business name, industry, and city are all required.");
    }
    setBusy(true);
    setCurrent(null);
    const toastId = toast.loading("Running 10 AI-search queries in parallel — 15-30 seconds…");
    try {
      const { data } = await api.post("/audit-console/run", form);
      setCurrent(data);
      toast.success(`Audit complete · ${data.score.hits}/${data.score.total} mentions (${data.score.grade})`, { id: toastId });
      await loadReports();
    } catch (err) {
      toast.error(formatApiError(err), { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Delete this audit report?")) return;
    try {
      await api.delete(`/audit-console/reports/${id}`);
      toast.success("Deleted");
      if (current?.id === id) setCurrent(null);
      await loadReports();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPrintable = (id) => window.open(`/audit-console/report/${id}`, "_blank");

  return (
    <div className="p-10" data-testid="audit-console-page">
      <Toaster position="top-right" richColors />

      <div className="mb-8">
        <div className="overline mb-3 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#E24E1B]" /> AI Visibility Audit Console
        </div>
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">
          Auto-run your 20-minute sales audit
        </h1>
        <p className="text-neutral-500 max-w-2xl">
          10 real web queries → real citations from Perplexity's Sonar engine (same source
          ChatGPT and Google AI Overviews draw from). Save reports, hand prospects the printable
          PDF, close from data instead of pitch.
        </p>
      </div>

      {configured === false && (
        <div className="mb-6 border border-amber-300 bg-amber-50 p-4 max-w-3xl flex items-start gap-3" data-testid="audit-console-needs-key">
          <TrendingUp className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold mb-1">Perplexity API key required</div>
            <p className="mb-2">Grab a free key at <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noreferrer" className="underline">perplexity.ai/settings/api</a> (free tier gets 5 requests/minute — perfect for 1 audit every 2 min). Paid $5/mo unlocks 60/min.</p>
            <Link to="/settings/audit-console" className="lf-btn-primary text-xs inline-flex items-center gap-1.5">
              Save Perplexity key →
            </Link>
          </div>
        </div>
      )}

      <form onSubmit={runAudit} className="bg-white border border-neutral-200 p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4 items-end" data-testid="audit-console-form">
        <div>
          <label className="overline block mb-2">Business name</label>
          <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
            placeholder="Acme Plumbing"
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
            data-testid="audit-console-business" />
        </div>
        <div>
          <label className="overline block mb-2">Industry / service</label>
          <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
            placeholder="plumber"
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
            data-testid="audit-console-industry" />
        </div>
        <div>
          <label className="overline block mb-2">City</label>
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="Long Beach, CA"
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
            data-testid="audit-console-city" />
        </div>
        <div className="md:col-span-4">
          <label className="overline block mb-2">#1 competitor (optional — enables overlay in report)</label>
          <input value={form.competitor_name || ""} onChange={(e) => setForm({ ...form, competitor_name: e.target.value })}
            placeholder="Ex: BigRival Plumbing"
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
            data-testid="audit-console-competitor" />
        </div>
        <button type="submit" disabled={busy || configured === false}
          className="lf-btn-primary flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="audit-console-run">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Running…</> : <><Search className="w-4 h-4" /> Run audit</>}
        </button>
      </form>

      {/* Bulk mode + progress */}
      <div className="mb-8 bg-white border border-neutral-200 p-4 flex items-center gap-4 flex-wrap" data-testid="audit-console-bulk-bar">
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1.5">
            <Users2 className="w-3 h-3" /> Bulk mode
          </div>
          <div className="text-xs text-neutral-500">
            Upload a CSV: <code className="bg-neutral-100 px-1">business_name,industry,city,prospect_email</code> · up to 100 rows · runs 3-way parallel to respect Perplexity rate limits.
          </div>
        </div>
        <input ref={fileRef} type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid="audit-console-csv-input" />
        <button onClick={() => fileRef.current?.click()} disabled={configured === false || bulkJob?.status === "running"}
          className="lf-btn-outline text-sm inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="audit-console-csv-btn">
          <Upload className="w-4 h-4" /> Upload CSV of prospects
        </button>
      </div>

      {bulkJob && (
        <div className="mb-8 bg-white border-2 border-[#E24E1B] p-4" data-testid="audit-console-bulk-status">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Bulk job {bulkJob.status === "running" ? "· running" : "· complete"}</div>
              <div className="font-mono text-sm">{bulkJob.completed} of {bulkJob.total} done{bulkJob.failed ? ` · ${bulkJob.failed} failed` : ""}</div>
            </div>
            {bulkJob.status !== "running" && (
              <button onClick={() => setBulkJob(null)} className="text-xs text-neutral-500 hover:text-black">Dismiss</button>
            )}
          </div>
          <div className="w-full h-2 bg-neutral-100 border border-neutral-200">
            <div className="h-full bg-[#E24E1B] transition-all" style={{ width: `${bulkJob.total ? (100 * bulkJob.completed / bulkJob.total) : 0}%` }} />
          </div>
        </div>
      )}

      {current && (
        <div className="bg-white border border-neutral-200 mb-8" data-testid="audit-console-report">
          <div className="p-6 border-b border-neutral-200 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="overline mb-2">Report · {new Date(current.created_at).toLocaleString()}</div>
              <h2 className="font-display text-2xl font-black">{current.business_name}</h2>
              <p className="text-sm text-neutral-500">{current.industry} · {current.city}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className={`px-4 py-3 border ${GRADE_TONE(current.score.grade)}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Score</div>
                <div className="font-display text-3xl font-black">{current.score.hits}/{current.score.total}</div>
                <div className="text-xs mt-0.5 font-semibold">{current.score.grade}</div>
              </div>
              <button onClick={() => openPrintable(current.id)} className="lf-btn-outline text-sm inline-flex items-center gap-2" data-testid="audit-console-print">
                <FileText className="w-4 h-4" /> Open printable report
              </button>
              {current.share_token && (
                <button onClick={() => {
                  const embed = `<div data-cor-audit-widget="${current.id}" data-cor-audit-token="${current.share_token}"></div>\n<script src="${window.location.origin}/api/audit/widget.js" async></script>`;
                  navigator.clipboard.writeText(embed).then(() => toast.success("Embed snippet copied — paste into client site")).catch(() => {});
                }} className="lf-btn-outline text-sm inline-flex items-center gap-2" title="Copy embeddable widget snippet">
                  <Copy className="w-4 h-4" /> Copy embed
                </button>
              )}
            </div>
          </div>

          {current.competitor_score && (
            <div className="p-4 border-b border-neutral-200 bg-neutral-50 flex items-center justify-between gap-4 flex-wrap" data-testid="audit-competitor-band">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">vs. {current.competitor_score.name}</div>
                <div className="text-sm">
                  You: <strong className="font-mono">{current.score.hits}/{current.score.total}</strong> ·
                  {" "}<span className="text-neutral-600">{current.competitor_score.name}:</span> <strong className="font-mono">{current.competitor_score.hits}/{current.competitor_score.total}</strong>
                </div>
              </div>
              <div className={`text-lg font-bold ${current.score.hits > current.competitor_score.hits ? "text-emerald-700" : current.score.hits < current.competitor_score.hits ? "text-rose-700" : "text-neutral-600"}`}>
                {current.score.hits > current.competitor_score.hits ? "🏆 You lead" : current.score.hits < current.competitor_score.hits ? "🚨 Losing" : "Tied"}
                {" "}({current.score.hits - current.competitor_score.hits > 0 ? "+" : ""}{current.score.hits - current.competitor_score.hits})
              </div>
            </div>
          )}

          {/* Top 3 fixes */}
          <div className="p-6 border-b border-neutral-200 bg-[#FFF6EE]">
            <div className="overline mb-3">Top 3 fixes (30-day plan)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {current.score.top_fixes.map((f, i) => (
                <div key={i} className="bg-white border border-neutral-200 p-3">
                  <div className="text-xs font-bold text-[#E24E1B] mb-1">Fix {i + 1}</div>
                  <div className="font-semibold text-sm mb-1">{f.title}</div>
                  <div className="text-xs text-neutral-600">{f.detail}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Query grid */}
          <div className="divide-y divide-neutral-200">
            {current.results.map((r, i) => (
              <div key={i} className="p-5 hover:bg-neutral-50" data-testid={`audit-result-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-0.5">Query {i + 1}</div>
                    <div className="font-mono text-sm font-semibold">{r.query}</div>
                  </div>
                  <div>
                    {r.mention?.mentioned ? (
                      <span className="pill pill-interested inline-flex items-center gap-1" title={r.mention.how}>
                        <Check className="w-3 h-3" /> Mentioned
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs bg-rose-50 border border-rose-200 text-rose-800 px-2 py-0.5">
                        <X className="w-3 h-3" /> Not in results
                      </span>
                    )}
                  </div>
                </div>
                {r.ok ? (
                  <>
                    <div className="text-xs text-neutral-600 whitespace-pre-wrap font-mono bg-neutral-50 border border-neutral-200 p-3">{r.answer || "(no answer text)"}</div>
                    {r.citations?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {r.citations.slice(0, 6).map((c, ci) => (
                          <a key={ci} href={c} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 underline hover:text-blue-800 inline-flex items-center gap-1">
                            <ExternalLink className="w-2.5 h-2.5" /> {new URL(c).hostname.replace("www.", "")}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-rose-700">Error: {r.error}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past reports */}
      <div className="bg-white border border-neutral-200" data-testid="audit-console-history">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="overline">Past audits ({reports.length})</div>
        </div>
        {reports.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 text-sm">No audits yet — run your first one above.</div>
        ) : (
          <table className="lf-grid">
            <thead>
              <tr>
                <th>Business</th>
                <th>City</th>
                <th>Industry</th>
                <th>Score</th>
                <th>Grade</th>
                <th>When</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} data-testid={`audit-history-${r.id}`}>
                  <td className="font-semibold">{r.business_name}</td>
                  <td className="text-xs">{r.city}</td>
                  <td className="text-xs">{r.industry}</td>
                  <td className="font-mono text-sm">{r.score?.hits ?? 0}/{r.score?.total ?? 10}</td>
                  <td><span className={`text-xs px-2 py-0.5 border ${GRADE_TONE(r.score?.grade || "F")}`}>{r.score?.grade || "—"}</span></td>
                  <td className="text-xs text-neutral-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => openPrintable(r.id)} className="text-xs text-neutral-600 hover:text-black inline-flex items-center gap-1 mr-2" title="Open printable report">
                      <FileText className="w-3.5 h-3.5" /> Open
                    </button>
                    <button onClick={() => copyShareLink(r)} className="text-xs text-neutral-600 hover:text-black inline-flex items-center gap-1 mr-2" title="Copy public share link">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => emailReport(r)} disabled={emailing} className="text-xs text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 mr-2" title="Email report to prospect">
                      <Mail className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteReport(r.id)} className="text-xs text-rose-600 hover:text-rose-800 inline-flex items-center gap-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
